const PROMPT_ENCRYPTION_ALG = 'RSA-OAEP-256+A256GCM';
const PROMPT_ENCRYPTION_VERSION = 1;

const getCrypto = () => {
  if (typeof window !== 'undefined' && window.crypto) {
    return window.crypto;
  }

  return null;
};

const encodeUtf8 = (value) => new TextEncoder().encode(value);

const decodeUtf8 = (bytes) => new TextDecoder().decode(bytes);

const binaryStringFromBytes = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(offset, offset + chunkSize)
    );
  }

  return binary;
};

const bytesFromBinaryString = (binary) => {
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const toBase64Url = (buffer) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);

  return btoa(binaryStringFromBytes(bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const fromBase64Url = (value) => {
  const normalized = String(value)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(String(value).length / 4) * 4, '=');

  return bytesFromBinaryString(atob(normalized));
};

const parsePublicJwk = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  if (typeof rawValue === 'object') {
    return rawValue;
  }

  const value = String(rawValue).trim();
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (jsonError) {
    return JSON.parse(decodeUtf8(fromBase64Url(value)));
  }
};

const getPublicJwk = () =>
  parsePublicJwk(process.env.GIOTTO_PROMPT_ENCRYPTION_PUBLIC_JWK);

export const getPromptEncryptionKeyId = (publicJwk) =>
  process.env.GIOTTO_PROMPT_ENCRYPTION_KEY_ID ||
  (publicJwk && publicJwk.kid) ||
  'default';

async function encryptTextForGiottoApi(value) {
  const publicJwk = getPublicJwk();
  if (!publicJwk) {
    return null;
  }

  const crypto = getCrypto();
  if (!crypto || !crypto.subtle || !crypto.getRandomValues) {
    throw new Error('Prompt encryption requires Web Crypto support');
  }

  const publicKey = await crypto.subtle.importKey(
    'jwk',
    publicJwk,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );

  const contentKey = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt']
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    contentKey,
    encodeUtf8(value)
  );
  const rawContentKey = await crypto.subtle.exportKey('raw', contentKey);
  const encryptedKey = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    rawContentKey
  );

  return {
    v: PROMPT_ENCRYPTION_VERSION,
    kid: getPromptEncryptionKeyId(publicJwk),
    alg: PROMPT_ENCRYPTION_ALG,
    encryptedKey: toBase64Url(encryptedKey),
    iv: toBase64Url(iv),
    ciphertext: toBase64Url(ciphertext),
  };
}

export async function encryptPayloadForGiottoApi(payload) {
  return encryptTextForGiottoApi(JSON.stringify(payload));
}

export async function buildEncryptedPayloadBody(payload) {
  const encryptedPayload = await encryptPayloadForGiottoApi(payload);
  return encryptedPayload ? { encryptedPayload } : payload;
}

export async function postEncryptedGiottoApiPayload(httpClient, url, payload) {
  return httpClient.post(url, await buildEncryptedPayloadBody(payload));
}

export async function encryptPromptForGiottoApi(prompt) {
  return encryptTextForGiottoApi(prompt);
}
